<div class="modal-header">
  <h5 class="modal-title" id="modal-basic-title">
    {{ 'players.player.addPlayer' | translate }}
  </h5>
  <button type="button" class="btn-close" (click)="closeModal()"></button>
</div>

@if (playerForm) {
  <form class="form theme-form" [formGroup]="playerForm" (ngSubmit)="onSubmit()">
    <div class="modal-body">
      <ng-container *ngTemplateOutlet="personalInfo"></ng-container>
      <ng-container *ngTemplateOutlet="contactInfo"></ng-container>
      <ng-container *ngTemplateOutlet="communicationPreferences"></ng-container>
      <ng-container *ngTemplateOutlet="otherInfo"></ng-container>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-danger" (click)="closeModal()">
        {{ 'btn.cancel' | translate }}
      </button>
      <button type="submit" class="btn btn-secondary">
        {{ 'btn.save' | translate }}
      </button>
    </div>
  </form>

  <ng-template #personalInfo>
    <div class="row g-2" [formGroup]="playerForm">
      <h5 class="border-bottom pb-2">{{ 'players.player.personalInfo' | translate }}</h5>

      <div class="form-group col-md-4">
        <label for="firstName" class="form-label">
          {{ 'players.player.firstName' | translate }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="firstName"
          formControlName="firstName"
          class="form-control" />
        <shared-form-field-error
          [control]="playerForm.get('firstName')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="lastName" class="form-label">
          {{ 'players.player.lastName' | translate }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="lastName"
          formControlName="lastName"
          class="form-control" />
        <shared-form-field-error
          [control]="playerForm.get('lastName')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="gender" class="form-label">
          {{ 'players.player.gender' | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-select
          id="gender"
          formControlName="gender"
          bindLabel="label"
          bindValue="value"
          [items]="genders$ | async"
          [placeholder]="'players.player.gender' | translate">
          <ng-template ng-option-tmp let-item="item">
            {{ item.label | translate }}
          </ng-template>
          <ng-template ng-label-tmp let-item="item">
            {{ item.label | translate }}
          </ng-template>
        </ng-select>
        <shared-form-field-error
          [control]="playerForm.get('gender')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="username" class="form-label">
          {{ 'players.player.username' | translate }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="username"
          formControlName="username"
          class="form-control"
          appLowercase />
        <shared-form-field-error
          [control]="playerForm.get('username')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="password" class="form-label">
          {{ 'players.player.password' | translate }}
          <span class="text-danger">*</span>
        </label>
        <div class="password-wrapper">
          <input
            id="password"
            formControlName="password"
            class="form-control"
            [type]="showPassword() ? 'text' : 'password'" />
          <i
            class="fas fa"
            [class.fa-eye]="showPassword()"
            [class.fa-eye-slash]="!showPassword()"
            (click)="togglePasswordVisibility()"></i>
        </div>
        <shared-form-field-error
          [control]="playerForm.get('password')"
          [errorMessages]="{
            required: 'players.player.passwordRequired',
            minlength: 'players.player.passwordMinLength',
            maxlength: 'players.player.passwordMaxLength',
          }"
          [errorParams]="{
            minlength: { min: PLAYER_REGISTER_VALIDATIONS.PASSWORD.MIN_LENGTH },
            maxlength: { max: PLAYER_REGISTER_VALIDATIONS.PASSWORD.MAX_LENGTH },
          }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="birthDate" class="form-label">
          {{ 'players.player.birthDate' | translate }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="date"
          id="birthDate"
          formControlName="birthDate"
          class="form-control" />
        <shared-form-field-error
          [control]="playerForm.get('birthDate')"
          [errorMessages]="{
            required: 'words.requiredField',
            underage: 'words.mustBeAdult',
          }">
        </shared-form-field-error>
      </div>
    </div>
  </ng-template>

  <ng-template #contactInfo>
    <div class="row g-2 mt-1" [formGroup]="playerForm">
      <h5 class="border-bottom pb-2">{{ 'players.player.contactInfo' | translate }}</h5>

      <div class="form-group col-md-4">
        <label for="countryId" class="form-label">
          {{ 'words.country' | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-select
          id="countryId"
          formControlName="countryId"
          bindLabel="label"
          bindValue="value"
          [items]="countries$ | async"
          [placeholder]="'words.country' | translate"
          (search)="onSearchSelect(NG_SELECT_QUERIES.COUNTRY, $event.term)"
          (clear)="onSearchSelect(NG_SELECT_QUERIES.COUNTRY)"
          (close)="onSearchSelect(NG_SELECT_QUERIES.COUNTRY)"
          (scrollToEnd)="onScrollToEndSelect(NG_SELECT_QUERIES.COUNTRIES)"
          (change)="onCountryChange($event)">
          <ng-template ng-option-tmp let-item="item">
            @if (item.icon) {
              <i class="flag-icon flag-icon-{{ item.icon | lowercase }} m-r-5"></i>
            }
            <span>{{ item.label }}</span>
          </ng-template>
          <ng-template ng-label-tmp let-item="item">
            @if (item.icon) {
              <i class="flag-icon flag-icon-{{ item.icon | lowercase }} m-r-5"></i>
            }
            <span>{{ item.label }}</span>
          </ng-template>
        </ng-select>
        <shared-form-field-error
          [control]="playerForm.get('countryId')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="cityId" class="form-label">
          {{ 'words.city' | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-select
          id="cityId"
          formControlName="cityId"
          bindLabel="label"
          bindValue="value"
          [items]="cities$ | async"
          [placeholder]="'words.city' | translate"
          (search)="onSearchSelect(NG_SELECT_QUERIES.CITIES, $event.term)"
          (clear)="onSearchSelect(NG_SELECT_QUERIES.CITIES)"
          (close)="onSearchSelect(NG_SELECT_QUERIES.CITIES)"
          (scrollToEnd)="onScrollToEndSelect(NG_SELECT_QUERIES.CITIES)"></ng-select>
        <shared-form-field-error
          [control]="playerForm.get('cityId')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="address" class="form-label">
          {{ 'players.player.address' | translate }}
        </label>
        <input
          type="text"
          id="address"
          formControlName="address"
          class="form-control" />
      </div>

      <div class="form-group col-md-4">
        <label for="zipCode" class="form-label">
          {{ 'players.player.zipCode' | translate }}
        </label>
        <input
          type="text"
          id="zipCode"
          formControlName="zipCode"
          class="form-control" />
      </div>

      <div class="form-group col-md-8">
        <label for="email" class="form-label">
          {{ 'userManagement.users.email' | translate }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="email"
          id="email"
          formControlName="email"
          class="form-control" />
        <shared-form-field-error
          [control]="playerForm.get('email')"
          [errorMessages]="{
            email: 'words.invalidEmail',
            required: 'words.requiredField',
          }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-4">
        <label for="telephoneCodeId" class="form-label">
          {{ 'userManagement.users.telephoneCode' | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-select
          id="telephoneCodeId"
          formControlName="telephoneCodeId"
          [bindLabel]="'label'"
          [bindValue]="'value'"
          [items]="countriesWithPhoneCodes$ | async"
          [clearable]="false"
          [notFoundText]="'words.noItemsFound' | translate"
          (search)="onSearchSelect(NG_SELECT_QUERIES.TELEPHONE_CODES, $event.term)"
          (scrollToEnd)="onScrollToEndSelect(NG_SELECT_QUERIES.TELEPHONE_CODES)"></ng-select>
        <shared-form-field-error
          [control]="playerForm.get('telephoneCodeId')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>

      <div class="form-group col-md-8">
        <label for="mobileNumber" class="form-label">
          {{ 'userManagement.users.mobileNumber' | translate }}
          <span class="text-danger">*</span>
        </label>
        <input
          type="text"
          id="mobileNumber"
          formControlName="mobileNumber"
          class="form-control"
          sharedPhoneInput />
        <shared-form-field-error
          [control]="playerForm.get('mobileNumber')"
          [errorMessages]="{
            invalidPhoneNumber: 'words.invalidPhoneNumber',
            required: 'words.requiredField',
          }">
        </shared-form-field-error>
      </div>
    </div>
  </ng-template>

  <ng-template #communicationPreferences>
    <div class="row g-2 mt-1" [formGroup]="playerForm">
      <h5 class="border-bottom pb-2">{{ 'players.player.communicationPreferences' | translate }}</h5>

      <div class="form-group col-md-6">
        <div class="checkbox pt-3">
          <input
            type="checkbox"
            id="callSubscription"
            formControlName="callSubscription"
            class="form-check-input" />
          <label for="callSubscription" class="form-check-label">
            {{ 'players.player.SubscribedToCall' | translate }}
          </label>
        </div>
        <div class="checkbox pt-3">
          <input
            type="checkbox"
            id="pushSubscription"
            formControlName="pushSubscription"
            class="form-check-input" />
          <label for="pushSubscription" class="form-check-label">
            {{ 'players.player.SubscribedToNotifications' | translate }}
          </label>
        </div>
        <div class="checkbox pt-3">
          <input
            type="checkbox"
            id="messageSubscription"
            formControlName="messageSubscription"
            class="form-check-input" />
          <label for="messageSubscription" class="form-check-label">
            {{ 'players.player.SubscribedToTextMessages' | translate }}
          </label>
        </div>
        <div class="checkbox pt-3">
          <input
            type="checkbox"
            id="mailSubscription"
            formControlName="mailSubscription"
            class="form-check-input" />
          <label for="mailSubscription" class="form-check-label">
            {{ 'players.player.SubscribedToEmail' | translate }}
          </label>
        </div>
      </div>

      <div class="form-group col-md-6">
        <label for="languageId" class="form-label">
          {{ 'players.player.lenguage' | translate }}
          <span class="text-danger">*</span>
        </label>
        <ng-select
          id="languageId"
          formControlName="languageId"
          bindLabel="label"
          bindValue="value"
          [items]="languages$ | async"
          [placeholder]="'players.player.lenguage' | translate"
          (search)="onSearchSelect(NG_SELECT_QUERIES.LANGUAGE, $event.term)"
          (clear)="onSearchSelect(NG_SELECT_QUERIES.LANGUAGE)"
          (close)="onSearchSelect(NG_SELECT_QUERIES.LANGUAGE)"
          (scrollToEnd)="onScrollToEndSelect(NG_SELECT_QUERIES.LANGUAGE)"></ng-select>
        <shared-form-field-error
          [control]="playerForm.get('languageId')"
          [errorMessages]="{ required: 'words.requiredField' }">
        </shared-form-field-error>
      </div>
    </div>
  </ng-template>

  <ng-template #otherInfo>
    <div class="row g-2 mt-1" [formGroup]="playerForm">
      <h5 class="border-bottom pb-2">{{ 'players.player.otherInfo' | translate }}</h5>

      @if (isCollaboratorAgent) {
        <div class="form-group col-md-6">
          <label for="agentId" class="form-label">
            {{ 'players.player.agentId' | translate }}
          </label>
          <input
            type="text"
            id="agentId"
            class="form-control disabled"
            [value]="profile()?.parentUsername"
            [readonly]="true" />
        </div>
      }

      @if (isAgentOrCashdesk) {
        <div class="form-group col-md-6">
          <label for="agentId" class="form-label">
            {{ 'players.player.agentId' | translate }}
          </label>
          <input
            type="text"
            id="agentId"
            class="form-control disabled"
            [value]="profile()?.username"
            [readonly]="true" />
        </div>
      }

      @if (canSelectAgent) {
        <div class="form-group col-md-12">
          <label for="agentId" class="form-label">
            {{ 'transfer.agent' | translate }}
            <span class="text-danger">*</span>
          </label>
          <ng-select
            id="agentId"
            formControlName="agentId"
            bindLabel="label"
            bindValue="value"
            [items]="users$ | async"
            [placeholder]="'transfer.placeholders.selectAgent' | translate"
            (change)="onUserChange($event)"
            (search)="onSearchSelect(NG_SELECT_QUERIES.USERS, $event.term)"
            (clear)="onSearchSelect(NG_SELECT_QUERIES.USERS)"
            (close)="onSearchSelect(NG_SELECT_QUERIES.USERS)"
            (scrollToEnd)="onScrollToEndSelect(NG_SELECT_QUERIES.USERS)"></ng-select>
          <shared-form-field-error
            [control]="playerForm.get('agentId')"
            [errorMessages]="{ required: 'words.requiredField' }">
          </shared-form-field-error>
        </div>
      }

      <div class="form-group">
        <div class="checkbox pt-3">
          <input
            type="checkbox"
            id="loyaltyProgram"
            formControlName="loyaltyProgram"
            class="form-check-input" />
          <label for="loyaltyProgram" class="form-check-label">
            {{ 'players.player.loyaltyProgram' | translate }}
          </label>
        </div>
      </div>

      <div class="form-group">
        <div class="checkbox pt-3">
          <input
            type="checkbox"
            [id]="PLAYER_FORM_CONTROLS.AGE_DECLARATION"
            [formControlName]="PLAYER_FORM_CONTROLS.AGE_DECLARATION"
            class="form-check-input" />
          <label [for]="PLAYER_FORM_CONTROLS.AGE_DECLARATION" class="form-check-label">
            {{ 'players.player.ageDeclaration' | translate }}
            <span class="text-danger">*</span>
          </label>
        </div>
        <shared-form-field-error
          [control]="playerForm.get(PLAYER_FORM_CONTROLS.AGE_DECLARATION)"
          [errorMessages]="{ required: 'players.player.ageDeclarationRequired' }">
        </shared-form-field-error>
      </div>
    </div>
  </ng-template>
}
